/* eslint-disable */
import React, { useState, useEffect } from "react";
import { message, Spin, Tag, Modal, Input, Button } from "antd";
import { SafetyOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { authApi } from "../../apis/authApi";

const TwoFactorSettings = () => {
  const [status, setStatus] = useState(null); // { enabled, method }
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null); // { qrDataUrl, otpauthUrl }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null); // string[] shown once

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setStatus(await authApi.twoFactorStatus());
    } catch {
      message.error("Failed to load 2FA status");
    } finally {
      setLoading(false);
    }
  };

  const beginSetup = async () => {
    try {
      setBusy(true);
      setSetup(await authApi.twoFactorSetup());
      setCode("");
    } catch {
      message.error("Could not start 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (!code.trim()) return;
    try {
      setBusy(true);
      const res = await authApi.twoFactorEnable(code.trim());
      setRecovery(res.recoveryCodes || []);
      setSetup(null);
      setCode("");
      await loadStatus();
      message.success("Two-factor authentication enabled");
    } catch (e) {
      message.error(e.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    try {
      setBusy(true);
      await authApi.twoFactorDisable();
      await loadStatus();
      message.success("Two-factor authentication disabled");
    } catch {
      message.error("Could not disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-0" style={{ borderRadius: 14 }}>
      <div className="card-body" style={{ padding: "28px 28px 24px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "#9ca3af",
            marginBottom: 12,
          }}
        >
          Two-Factor Authentication
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div
                className="profile-field-ico"
                style={{
                  background: status?.enabled ? "#dcfce7" : "#FDF3E3",
                  color: status?.enabled ? "#16a34a" : "#7B4D12",
                }}
              >
                <SafetyOutlined />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1a1d2e" }}>
                  Authenticator app (TOTP)
                </div>
                <div style={{ fontSize: 13, color: "#9ca3af" }}>
                  Add a second step at login using Google Authenticator, Authy, etc.
                </div>
              </div>
              {status?.enabled ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  Enabled
                </Tag>
              ) : (
                <Tag color="default">Disabled</Tag>
              )}
            </div>

            {/* Controls */}
            {status?.enabled ? (
              <Button danger onClick={disable} loading={busy}>
                Disable 2FA
              </Button>
            ) : setup ? (
              <div
                style={{
                  border: "1px solid #f3f4f6",
                  borderRadius: 10,
                  padding: 20,
                  textAlign: "center",
                }}
              >
                <p style={{ color: "#4a3724", marginBottom: 12 }}>
                  1. Scan this QR code with your authenticator app, then enter the
                  6-digit code it shows.
                </p>
                <img
                  src={setup.qrDataUrl}
                  alt="2FA QR code"
                  style={{ width: 180, height: 180, margin: "0 auto 16px" }}
                />
                <div style={{ maxWidth: 240, margin: "0 auto" }}>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    onPressEnter={confirmEnable}
                    style={{ textAlign: "center", letterSpacing: 4 }}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <Button onClick={() => setSetup(null)} disabled={busy} block>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={confirmEnable}
                      loading={busy}
                      disabled={!code.trim()}
                      block
                    >
                      Verify &amp; Enable
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button type="primary" onClick={beginSetup} loading={busy}>
                Enable 2FA
              </Button>
            )}
          </>
        )}
      </div>

      {/* Recovery codes (shown once) */}
      <Modal
        open={!!recovery}
        onOk={() => setRecovery(null)}
        onCancel={() => setRecovery(null)}
        okText="I've saved them"
        cancelButtonProps={{ style: { display: "none" } }}
        title="Save your recovery codes"
        maskClosable={false}
      >
        <p style={{ color: "#6b7280" }}>
          Store these somewhere safe. Each code can be used once if you lose access
          to your authenticator app. They won't be shown again.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            fontFamily: "monospace",
            fontSize: 15,
            background: "#f9fafb",
            border: "1px solid #f3f4f6",
            borderRadius: 8,
            padding: 16,
          }}
        >
          {(recovery || []).map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default TwoFactorSettings;
